import { DraftVersionConflictError } from '../../services/unifiedEmailAction.service';

interface DraftVersionChain {
  head: string;
  versions: Set<string>;
}

const chains = new Map<string, DraftVersionChain>();
const draftIdByLineage = new Map<string, string>();

export const recordDraftWrite = (
  draftId: string,
  expectedVersion: string | undefined,
  newVersion: string,
): void => {
  const chain = chains.get(draftId) ?? {
    head: newVersion,
    versions: new Set<string>(),
  };
  if (expectedVersion) {
    chain.versions.add(expectedVersion);
  }
  chain.versions.add(newVersion);
  chain.head = newVersion;
  chains.set(draftId, chain);
};

export const forgetDraftVersions = (draftId: string): void => {
  chains.delete(draftId);
  for (const [lineageId, id] of draftIdByLineage) {
    if (id === draftId) {
      draftIdByLineage.delete(lineageId);
    }
  }
};

// Once any session of a lineage created the draft, every later write of that lineage updates it.
export const recordLineageDraft = (
  lineageId: string,
  draftId: string,
): void => {
  draftIdByLineage.set(lineageId, draftId);
};

export const getLineageDraftId = (lineageId: string): string | undefined =>
  draftIdByLineage.get(lineageId);

export const getDraftHeadVersion = (draftId: string): string | undefined =>
  chains.get(draftId)?.head;

export const isOwnDraftVersion = (draftId: string, version: string): boolean =>
  chains.get(draftId)?.versions.has(version) ?? false;

// A version this tab produced or superseded is behind the head only because another compose wrote since.
export const fastForwardDraftVersion = (
  draftId: string,
  version: string | undefined,
): string | undefined => {
  const chain = chains.get(draftId);
  return version && chain?.versions.has(version) ? chain.head : version;
};

export const getOwnConflictVersion = (
  draftId: string,
  error: unknown,
  sentVersion: string | undefined,
): string | undefined => {
  if (!(error instanceof DraftVersionConflictError)) {
    return undefined;
  }
  const { currentDraftVersion } = error;
  return currentDraftVersion &&
    currentDraftVersion !== sentVersion &&
    isOwnDraftVersion(draftId, currentDraftVersion)
    ? currentDraftVersion
    : undefined;
};

export const resetDraftVersionLedger = (): void => {
  chains.clear();
  draftIdByLineage.clear();
};
